/**
 * TermsPage — public End User License Agreement page.
 *
 * Rendered OUTSIDE DatabaseProvider so it's accessible without login.
 * Ported from v1's src/pages/terms.tsx with v2 Tailwind styling.
 */

import { useTranslation } from "react-i18next"
import ArrowLeftIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/arrow-left.svg?react"

export function TermsPage() {
  const { t } = useTranslation()

  return (
    <div className="h-dvh overflow-auto bg-background px-6 py-4 sm:px-10">
      <div className="mx-auto max-w-2xl">
        <a
          href="/"
          className="mb-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-3.5" />
          {t("Go to To To-Do")}
        </a>

        <h1 className="mb-6 text-2xl font-bold">
          {t("End User License Agreement")}
        </h1>

        <div className="prose prose-sm max-w-none text-foreground [&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_p]:mb-3 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-muted-foreground">
          <p>Last Updated 2024-01-25</p>

          <p>
            This End User License Agreement (&quot;Agreement&quot;) is entered
            into between Zenta AB, a Swedish company with its principal place of
            business at Oxtorget 4, 352 32, Växjö (&quot;Licensor,&quot;
            &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), and you
            (&quot;User&quot; or &quot;you&quot;) regarding the use of To To-Do
            (the &quot;App&quot;).
          </p>

          <h2>1. Acceptance of Terms</h2>

          <p>
            By downloading, installing, or using the App, you agree to be bound
            by the terms and conditions of this Agreement. If you do not agree to
            these terms, do not use the App.
          </p>

          <h2>2. License Grant</h2>

          <p>
            Licensor grants you a limited, non-exclusive, non-transferable,
            revocable license to use the App solely for your personal,
            non-commercial purposes in accordance with this Agreement.
          </p>

          <h2>3. Restrictions</h2>

          <p>You shall not:</p>

          <p>
            a. Modify, adapt, translate, reverse engineer, decompile,
            disassemble, or create derivative works based on the App.
          </p>

          <p>
            b. Use the App for any unlawful purpose or in any manner inconsistent
            with this Agreement.
          </p>

          <p>
            c. Remove, alter, or obscure any proprietary notices or labels on the
            App.
          </p>

          <h2>4. Intellectual Property</h2>

          <p>
            The App and all intellectual property rights, including but not
            limited to copyrights, trademarks, and trade secrets, are owned by
            Licensor and its licensors. You agree not to infringe on these
            rights.
          </p>

          <h2>5. Privacy</h2>

          <p>
            Your use of the App is also governed by our Privacy Policy, available
            at{" "}
            <a
              href="/privacy"
              className="text-primary hover:underline"
            >
              totodo.app/privacy
            </a>
            . By using the App, you consent to the collection and use of your
            information as described in the Privacy Policy.
          </p>

          <h2>6. Warranty Disclaimer</h2>

          <p>
            The App is provided &quot;as is&quot; without any warranty of any
            kind, either express or implied. We do not warrant that the App will
            be error-free or uninterrupted.
          </p>

          <h2>7. Limitation of Liability</h2>

          <p>
            To the extent permitted by law, Licensor shall not be liable for any
            direct, indirect, incidental, special, or consequential damages
            arising out of or in any way connected with the use of the App.
          </p>

          <h2>8. Termination</h2>

          <p>
            Licensor may terminate this Agreement at any time if you breach any
            provision. Upon termination, you must cease all use of the App.
          </p>

          <h2>9. Governing Law</h2>

          <p>
            This Agreement is governed by the laws of Sweden. Any disputes
            arising out of or in connection with this Agreement shall be resolved
            by the courts of Sweden.
          </p>

          <h2>10. Contact information</h2>

          <p>
            If you have any questions or concerns about this Agreement, you may
            contact us at{" "}
            <a
              href="mailto:info@zenta.se"
              className="text-primary hover:underline"
            >
              info@zenta.se
            </a>
            .
          </p>

          <p>
            By using the App, you acknowledge that you have read, understood, and
            agree to be bound by this Agreement.
          </p>
        </div>
      </div>
    </div>
  )
}
